import { ClassificationsProviderV2 } from '@/providers/classification/classifications-v2.provider';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';
import { TranslateParser } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import type { HTMLFreestyleSettings, HTMLFreestyleTranslationKeys } from '../../../interfaces/Widget.interface';
import { SafePipe } from '../../../pipes/safe.pipe';
import { BuildingsProvider } from '../../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { ProjectsProvider } from '../../../providers/projects/projects.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { AbstractWidgetClass } from '../AbstractWidget.class';
import { WidgetCardComponent } from '../widget-card/widget-card';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-html-freestyle.scss'],
    selector: 'widget-html-freestyle',
    templateUrl: 'widget-html-freestyle.html',
    imports: [WidgetCardComponent, AsyncPipe, SafePipe]
})
export class WidgetHtmlFreestyleComponent
    extends AbstractWidgetClass<HTMLFreestyleTranslationKeys, HTMLFreestyleSettings>
    implements OnInit
{
    private _dashboardingProvider = inject(DashboardingProvider);
    private _buildingsProvider = inject(BuildingsProvider);
    private _projectsProvider = inject(ProjectsProvider);
    private _parser = inject(TranslateParser);
    private _classificationsProvider = inject(ClassificationsProviderV2);

    size = WidgetSize.FULL;

    html$!: Observable<string>;

    ngOnInit() {
        this.html$ = combineLatest(
            this._dashboardingProvider.getMeaningfulBuildingId$().pipe(
                switchMap(buildingId =>
                    combineLatest(this._buildingsProvider.getBuilding$(buildingId)).pipe(
                        map(([building]) => {
                            return {
                                ...this.generateObjectProperties(building, 'building')
                            };
                        })
                    )
                )
            ),
            this._projectsProvider.getUserCurrentProject$().pipe(map(project => this.generateObjectProperties(project, 'project'))),
            this._dashboardingProvider.getSelectedNodeId$().pipe(
                switchMap(nodeId => this._classificationsProvider.getClassificationNodeById$(nodeId)),
                map(classificationNode => (classificationNode ? this.generateObjectProperties(classificationNode, 'classification') : {}))
            )
        ).pipe(
            map(([buildingParams, projectParams, classificationParams]): Record<string, string> => {
                return {
                    ...buildingParams,
                    ...projectParams,
                    ...classificationParams
                };
            }),
            map(interpolationParams => {
                return this._parser.interpolate(this.config.translations!.htmlToInterpolate, { ...interpolationParams })!;
            }),
            distinctUntilChanged()
        );
    }

    generateObjectProperties(object: any, prefix: string): any {
        const params: any = {};
        Object.entries(object || {}).forEach(([key, value]) => {
            if (['string', 'number', 'boolean'].includes(typeof value)) {
                params[(prefix + '_' + key).split(' ').join('_')] = value;
            }
        });
        return params;
    }
}
